import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Check, CircleDot, ClipboardList, Flag, MoreHorizontal, Rocket, Shapes, Tag, Tags, Target, type LucideIcon } from 'lucide-react';
import { flyToCompleted } from '../../lib/mark-done';
import { ApiError, apiFetch, v1Path } from '../../lib/api';
import { isActivityDto, type ActivityDto } from '../../lib/activity-timeline';
import ActivityTimeline from './ActivityTimeline';
import { parseItems, memberDisplayName, WORK_ITEM_TYPES, workItemSubtypeLabel, workItemTypeLabel, type StateDto, type WorkItemFull } from '../../lib/work-types';
import { isCommentDto, type CommentDto } from '../../lib/comments';
import { isReleaseLocked, type ReleaseSlim } from '../../lib/releases';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import RichTextField from './RichTextField';
import AttachmentList from './AttachmentList';
import CommentThread from './CommentThread';
import WorkItemFamily from './WorkItemFamily';
import WorkItemRelations from './WorkItemRelations';
import { isRichTextEmpty } from '../../lib/rich-text';
import { uploadPendingAttachment } from '../../lib/attachments';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Separator } from '../ui/separator';
import TitleAutosave from './TitleAutosave';
import { useOverlays } from './overlay-context';
import { useProjectDecorations } from './use-project-decorations';
import WorkItemPropertyActions from './WorkItemPropertyActions';
import { SERVICE_REQUEST_CREATE_PATH } from '../../lib/service-requests';
import InitiativePicker from './InitiativePicker';
import { TagChip, TagPicker, useTags } from './TagPicker';
import type { TagDto } from '../../lib/tags';
import BlockedControl from './BlockedControl';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { cn } from '../../lib/utils';

type Props = {
  projectId: string;
  item: WorkItemFull;
  states: StateDto[];
  onUpdated: (item: WorkItemFull) => void;
  chrome: 'peek' | 'page';
  /** Runs once a task has been marked done and its tick has reached Completed. */
  onMarkedDone?: () => void;
};

function PropertyRow({
  icon: Icon,
  label,
  htmlFor,
  children,
}: {
  icon: LucideIcon;
  label: string;
  htmlFor?: string;
  children: ReactNode;
}) {
  return (
    <div className="grid grid-cols-[1rem_5.5rem_minmax(0,1fr)] items-center gap-2">
      <Icon className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
      <Label htmlFor={htmlFor} className="text-xs font-normal text-muted-foreground">
        {label}
      </Label>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

export default function WorkItemDetail({ projectId, item, states, onUpdated, chrome, onMarkedDone }: Props) {
  const { toast, confirm, pushBlock, popBlock } = useOverlays();
  const allTags = useTags();
  const [comments, setComments] = useState<CommentDto[]>([]);
  const [activity, setActivity] = useState<ActivityDto[]>([]);
  // An initiative's launch step can be read by anyone; only its stream's members change it.
  const outsider = item.viewer_is_member === false;
  const { labels, members, subtypes } = useProjectDecorations(outsider ? undefined : projectId);
  const canLinkInitiative = item.type === 'task' && item.parent_id === null;
  const [releases, setReleases] = useState<ReleaseSlim[]>([]);
  const [body, setBody] = useState('');
  const [description, setDescription] = useState(item.description);
  const descriptionRef = useRef(description);
  descriptionRef.current = description;
  const [submitting, setSubmitting] = useState(false);
  const archived = Boolean(item.archived_at);
  const readOnly = archived || outsider;
  const canCreateDeviceDeploymentDraft = !archived && item.parent_id === null && item.key.startsWith('SOPS-');
  const currentReleaseId = item.release_id ?? item.release?.id ?? null;
  const currentReleaseStatus =
    item.release?.status ?? releases.find((release) => release.id === currentReleaseId)?.status;
  const releaseLocked = isReleaseLocked(currentReleaseStatus);
  const blockHeld = useRef(0);
  const holdBlock = useCallback(() => {
    blockHeld.current += 1;
    pushBlock();
  }, [pushBlock]);
  const releaseBlock = useCallback(() => {
    if (blockHeld.current < 1) return;
    blockHeld.current -= 1;
    popBlock();
  }, [popBlock]);
  useEffect(() => {
    return () => {
      while (blockHeld.current > 0) {
        releaseBlock();
      }
    };
  }, [releaseBlock]);

  const loadSide = useCallback(async () => {
    if (outsider) return;
    const [commentPage, activityPage] = await Promise.all([
      apiFetch<unknown>(v1Path(`/projects/${projectId}/work-items/${item.id}/comments`)),
      apiFetch<unknown>(v1Path(`/projects/${projectId}/work-items/${item.id}/activity`)),
    ]);
    setComments(parseItems(commentPage, isCommentDto, 'comments'));
    setActivity(parseItems(activityPage, isActivityDto, 'activity'));
  }, [projectId, item.id, outsider]);

  useEffect(() => {
    void loadSide().catch(() => undefined);
  }, [loadSide]);

  useEffect(() => {
    let cancelled = false;
    void apiFetch<{ items: ReleaseSlim[] }>(v1Path('/releases'))
      .then((res) => {
        if (!cancelled) setReleases(res.items);
      })
      .catch(() => {
        if (!cancelled) setReleases([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setDescription(item.description);
  }, [item.id, item.description]);

  async function patchFields(fields: Record<string, unknown>) {
    try {
      const dto = await apiFetch<WorkItemFull>(
        v1Path(`/projects/${projectId}/work-items/${item.id}`),
        { method: 'PATCH', body: JSON.stringify({ version: item.version, ...fields }) },
      );
      onUpdated(dto);
      window.dispatchEvent(new CustomEvent('bb:work-items-changed'));
      void loadSide().catch(() => undefined);
      return dto;
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const fresh = await apiFetch<WorkItemFull>(
          v1Path(`/projects/${projectId}/work-items/${item.id}`),
        );
        onUpdated(fresh);
        toast('Someone else updated this item. Reloaded.');
        return fresh;
      }
      toast(err instanceof Error ? err.message : 'Update failed');
      throw err;
    }
  }

  async function saveDescription() {
    if (readOnly) return;
    const next = descriptionRef.current;
    if (next === item.description) return;
    await patchFields({ description: next });
  }

  async function submitComment(event: FormEvent) {
    event.preventDefault();
    if (isRichTextEmpty(body) || readOnly) return;
    setSubmitting(true);
    try {
      await apiFetch(v1Path(`/projects/${projectId}/work-items/${item.id}/comments`), {
        method: 'POST',
        body: JSON.stringify({ body }),
      });
      setBody('');
      await loadSide();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  }

  const isInitiative = item.type === 'initiative';

  async function archiveInitiative() {
    const ok = await confirm({ title: 'Archive initiative', description: `Archive ${item.key}?`, confirmLabel: 'Archive' });
    if (!ok) return;
    try {
      const dto = await apiFetch<WorkItemFull>(
        v1Path(`/projects/${projectId}/work-items/${item.id}/archive`),
        { method: 'POST', body: JSON.stringify({ version: item.version }) },
      );
      onUpdated({ ...item, ...dto, description: item.description });
      toast('Archived');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not archive it');
    }
  }

  const markDoneRef = useRef<HTMLButtonElement>(null);
  const [markingDone, setMarkingDone] = useState<'idle' | 'saving' | 'done'>('idle');

  async function markDone() {
    if (markingDone !== 'idle') return;
    setMarkingDone('saving');
    let dto: WorkItemFull;
    try {
      dto = await apiFetch<WorkItemFull>(
        v1Path(`/projects/${projectId}/work-items/${item.id}/archive`),
        { method: 'POST', body: JSON.stringify({ version: item.version }) },
      );
    } catch (err) {
      setMarkingDone('idle');
      toast(err instanceof Error ? err.message : 'Could not mark it done');
      return;
    }
    setMarkingDone('done');
    await flyToCompleted(markDoneRef.current, projectId);
    onUpdated({ ...item, ...dto, description: item.description });
    setMarkingDone('idle');
    window.dispatchEvent(new CustomEvent('bb:work-items-changed'));
    toast(`Marked as done: ${item.title}`);
    onMarkedDone?.();
  }

  async function onUnarchive() {
    try {
      const dto = await apiFetch<WorkItemFull>(
        v1Path(`/projects/${projectId}/work-items/${item.id}/unarchive`),
        { method: 'POST', body: JSON.stringify({ version: item.version }) },
      );
      onUpdated({ ...item, ...dto, description: item.description });
      toast(isInitiative ? 'Restored from archive' : 'Reopened');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not reopen it');
    }
  }

  async function onDelete() {
    const ok = await confirm({
      title: 'Delete work item',
      description: `Delete ${item.key}? This cannot be undone from the list.`,
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await apiFetch(v1Path(`/projects/${projectId}/work-items/${item.id}`), { method: 'DELETE' });
      toast('Deleted');
      window.dispatchEvent(new CustomEvent('bb:work-items-changed'));
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Delete failed');
    }
  }

  const fieldTrigger = 'h-8 border-transparent bg-transparent shadow-none hover:bg-accent';
  const itemTags = (item.tag_ids ?? []).map((id) => allTags.find((tag) => tag.id === id)).filter((tag): tag is TagDto => Boolean(tag));
  const mentionSuggestions = useMemo(
    () =>
      members.map((member) => ({
        id: member.user_id,
        label: memberDisplayName(member),
      })),
    [members],
  );
  const uploadImage = useCallback(
    (file: File) => uploadPendingAttachment(file).then((row) => row.download_url),
    [],
  );

  const doneAction = outsider ? null : archived ? (
    <Button type="button" size="sm" variant="outline" onClick={() => void onUnarchive()}>
      {isInitiative ? 'Unarchive' : 'Reopen'}
    </Button>
  ) : isInitiative ? (
    <Button type="button" size="sm" onClick={() => void archiveInitiative()}>
      Archive
    </Button>
  ) : (
    <Button
      ref={markDoneRef}
      type="button"
      size="sm"
      disabled={markingDone !== 'idle'}
      onClick={() => void markDone()}
      className={cn(
        'gap-1.5 transition-colors duration-200 disabled:opacity-100',
        markingDone === 'done' && 'bg-emerald-600 text-white hover:bg-emerald-600',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'flex h-4 w-4 items-center justify-center rounded-full border-[1.5px] border-current transition-transform duration-300 ease-out motion-reduce:transition-none',
          markingDone === 'done' ? 'scale-110 border-white bg-white text-emerald-600' : 'scale-100',
        )}
      >
        <Check className={cn('h-2.5 w-2.5', markingDone === 'done' ? 'opacity-100' : 'opacity-0')} strokeWidth={3.5} />
      </span>
      {markingDone === 'done' ? 'Done' : 'Mark as done'}
    </Button>
  );

  const properties = (
    <div className="space-y-3">
      <PropertyRow icon={Shapes} label="Type" htmlFor="work-item-type">
        <Select
          value={item.type}
          disabled={readOnly}
          onValueChange={(value) => void patchFields({ type: value })}
        >
          <SelectTrigger id="work-item-type" className={fieldTrigger}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {WORK_ITEM_TYPES.filter(
              (type) =>
                type !== 'service_request' &&
                (type !== 'initiative' || item.type === 'initiative'),
            ).map((type) => (
              <SelectItem key={type} value={type}>
                {workItemTypeLabel(type)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </PropertyRow>
      <PropertyRow icon={Tag} label="Task type" htmlFor="work-item-subtype">
        <Select
          value={item.subtype ?? '__none__'}
          disabled={readOnly}
          onValueChange={(value) => void patchFields({ subtype: value === '__none__' ? null : value })}
        >
          <SelectTrigger id="work-item-subtype" className={fieldTrigger}>
            <SelectValue placeholder="None" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">None</SelectItem>
            {item.subtype && !subtypes.some((subtype) => subtype.key === item.subtype) ? (
              <SelectItem value={item.subtype}>
                {workItemSubtypeLabel(item.subtype, subtypes)} (not configured)
              </SelectItem>
            ) : null}
            {subtypes.map((subtype) => (
              <SelectItem key={subtype.key} value={subtype.key}>
                {subtype.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </PropertyRow>
      <PropertyRow icon={CircleDot} label="Status" htmlFor="work-item-state">
        <Select
          value={item.state_id}
          disabled={readOnly}
          onValueChange={(value) => void patchFields({ state_id: value })}
        >
          <SelectTrigger id="work-item-state" className={fieldTrigger}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {states.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {s.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </PropertyRow>
      <PropertyRow icon={Rocket} label="Release" htmlFor="work-item-release">
        <Select
          value={currentReleaseId ?? '__none__'}
          disabled={readOnly || releaseLocked}
          onValueChange={(value) => void patchFields({ release_id: value === '__none__' ? null : value })}
        >
          <SelectTrigger id="work-item-release" className={fieldTrigger}>
            <SelectValue placeholder="None" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">None</SelectItem>
            {releases.map((release) => (
              <SelectItem
                key={release.id}
                value={release.id}
                disabled={isReleaseLocked(release.status)}
              >
                {release.name}
                {release.tag ? ` · ${release.tag}` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </PropertyRow>
      {canLinkInitiative ? (
        <PropertyRow icon={Target} label="Initiative">
          <InitiativePicker
            value={item.initiative_id ?? null}
            current={item.initiative ?? null}
            disabled={readOnly}
            className={fieldTrigger}
            onChange={(initiativeId) => void patchFields({ initiative_id: initiativeId })}
          />
        </PropertyRow>
      ) : null}
      <PropertyRow icon={Tags} label="Tags">
        <TagPicker
          workItemId={item.id}
          value={item.tag_ids ?? []}
          disabled={readOnly}
          onChange={(tagIds) => onUpdated({ ...item, tag_ids: tagIds })}
        >
          <button
            type="button"
            disabled={readOnly}
            className={cn('flex min-h-8 w-full flex-wrap items-center gap-1 rounded-md px-3 py-1 text-left text-sm', !readOnly && 'hover:bg-accent')}
          >
            {itemTags.length === 0 ? (
              <span className="text-muted-foreground">{readOnly ? 'None' : 'Add tags'}</span>
            ) : (
              itemTags.map((tag) => <TagChip key={tag.id} tag={tag} />)
            )}
          </button>
        </TagPicker>
      </PropertyRow>
      <PropertyRow icon={Flag} label="Priority" htmlFor="work-item-priority">
        <Select
          value={item.priority}
          disabled={readOnly}
          onValueChange={(value) => void patchFields({ priority: value })}
        >
          <SelectTrigger id="work-item-priority" data-shortcut="priority" className={fieldTrigger}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {['urgent', 'high', 'medium', 'low', 'none'].map((p) => (
              <SelectItem key={p} value={p}>
                {p === 'none' ? 'No priority' : p.charAt(0).toUpperCase() + p.slice(1)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </PropertyRow>
      {outsider ? null : (
      <div className="flex flex-wrap items-center gap-2 pt-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" size="icon" variant="ghost" className="h-8 w-8" aria-label="More actions">
              <MoreHorizontal className="h-4 w-4" aria-hidden />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem className="text-destructive" onSelect={() => void onDelete()}>
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      )}
    </div>
  );

  return (
    <div
      className={
        chrome === 'page'
          ? 'lg:grid lg:grid-cols-[minmax(0,1fr)_18rem] lg:items-start lg:gap-12'
          : 'space-y-6'
      }
    >
      <div className="space-y-6">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <p className="font-mono text-xs text-muted-foreground">{item.key}</p>
            {archived ? <Badge variant="outline">{isInitiative ? 'Archived' : 'Done'}</Badge> : null}
            {chrome === 'peek' && !outsider ? (
              <Button asChild variant="link" size="sm" className="ml-auto h-auto p-0">
                <Link to={`/projects/${projectId}/work-items/${item.id}`}>Open full page</Link>
              </Button>
            ) : null}
          </div>
          {outsider ? (
            <p className="rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground" data-testid="outsider-note">
              You can view this launch step, but only members of its stream can change it.
            </p>
          ) : null}
          <TitleAutosave
            projectId={projectId}
            item={item}
            onUpdated={onUpdated}
            readOnly={readOnly}
          />
          <WorkItemPropertyActions
            item={item}
            finished={archived || states.find((s) => s.id === item.state_id)?.group === 'cancelled'}
            labels={labels}
            members={members}
            readOnly={readOnly}
            onPatch={patchFields}
          />
          {item.blocked_reason ? (
            <>
              {doneAction ? <div className="flex flex-wrap items-center gap-2">{doneAction}</div> : null}
              <BlockedControl item={item} members={members} readOnly={readOnly} onPatch={patchFields} />
            </>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              {doneAction}
              <BlockedControl item={item} members={members} readOnly={readOnly} onPatch={patchFields} />
            </div>
          )}
          {canCreateDeviceDeploymentDraft ? (
            <Button asChild type="button" size="sm" variant="outline">
              <Link
                to={SERVICE_REQUEST_CREATE_PATH}
                state={{
                  source: {
                    kind: 'sales_ops',
                    key: item.key,
                    title: item.title,
                    workItemId: item.id,
                    projectId,
                  },
                }}
              >
                <ClipboardList className="h-4 w-4" aria-hidden />
                Create Device Deployment draft
              </Link>
            </Button>
          ) : null}
        </div>

        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">Description</Label>
          <RichTextField
            value={description}
            readOnly={readOnly}
            mentions={mentionSuggestions}
            upload={uploadImage}
            onUploadError={toast}
            onFocus={holdBlock}
            onBlur={() => {
              releaseBlock();
              void saveDescription();
            }}
            onChange={(html) => {
              descriptionRef.current = html;
              setDescription(html);
            }}
          />
        </div>

        {outsider ? null : <AttachmentList projectId={projectId} workItemId={item.id} readOnly={readOnly} />}

        <WorkItemFamily
          projectId={projectId}
          item={item}
          readOnly={readOnly}
          onUpdated={onUpdated}
          subtypes={subtypes}
          members={members}
        />

        {outsider ? null : (
          <WorkItemRelations
            projectId={projectId}
            workItemId={item.id}
            workItemType={item.type}
            readOnly={readOnly}
          />
        )}

        {chrome === 'peek' ? properties : null}

        {chrome === 'peek' ? <Separator /> : null}

        {outsider ? null : (
        <>
        <section className="space-y-3">
          <h2 className="text-sm font-medium">Comments</h2>
          <form onSubmit={submitComment} className="space-y-2">
            <RichTextField
              variant="compact"
              value={body}
              readOnly={readOnly}
              placeholder="Write a comment. Type @ to mention someone."
              mentions={mentionSuggestions}
              upload={uploadImage}
              onUploadError={toast}
              onFocus={holdBlock}
              onBlur={releaseBlock}
              onChange={setBody}
            />
            <Button type="submit" size="sm" disabled={submitting || isRichTextEmpty(body) || readOnly}>
              Post comment
            </Button>
          </form>
          <CommentThread
            comments={comments}
            members={members}
            mentionSuggestions={mentionSuggestions}
          />
        </section>

        <section className="space-y-3">
          <h2 className="text-sm font-medium">Activity</h2>
          <ActivityTimeline
            key={item.id}
            activity={activity}
            members={members}
            labels={labels}
            formatOptions={{ subtypes }}
          />
        </section>
        </>
        )}
      </div>
      {chrome === 'page' ? (
        <aside className="mt-8 space-y-4 lg:mt-0 lg:border-l lg:pl-8">
          {properties}
        </aside>
      ) : null}
    </div>
  );
}
